import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Communication, General } from 'icons/untitledui';
import colours from 'theme/atoms/colours';

import { EmailComposeMenu } from 'shared/components/EmailComposeMenu/_index';
import { ReflexStep } from 'shiftNotes/_types';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';

import * as Styled from 'shiftNotes/styles/InstantReflex/ReflexStepCard.style';
import ReflexStepCard from './ReflexStepCard';

interface ComposeMessageCardProps {
  step: ReflexStep;
  stepNumber: number;
  totalSteps: number;
  noteId: string;
  reflexId: string;
  label?: string;
}

const COPIED_FEEDBACK_MS = 2000;

const SUBJECT_PREFIX_REGEX = /^\s*subject\s*:\s*/i;

function extractSubjectAndBody(body: string): {
  subject: string;
  emailBody: string;
} {
  const trimmed = body.trim();
  if (!trimmed) return { subject: '', emailBody: '' };

  const lines = trimmed.split('\n');
  const firstLine = lines[0].trim();
  if (!SUBJECT_PREFIX_REGEX.test(firstLine)) {
    return { subject: '', emailBody: trimmed };
  }

  const subject = firstLine.replace(SUBJECT_PREFIX_REGEX, '').trim();
  const rest = lines.slice(1).join('\n').replace(/^\n+/, '');
  return { subject, emailBody: rest };
}

export default function ComposeMessageCard({
  step,
  stepNumber,
  totalSteps,
  noteId,
  reflexId,
  label,
}: ComposeMessageCardProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  const shiftNotesCapture = usePosthogCapture();

  const [justCopied, setJustCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    },
    []
  );

  const messageBody = step.body ?? '';
  const { subject, emailBody } = extractSubjectAndBody(messageBody);
  const hasMessage = messageBody.trim().length > 0;

  const captureProperties = {
    shift_note_id: noteId,
    reflex_id: reflexId,
    step_id: step.id,
  };

  const handleCopy = () => {
    if (!hasMessage) return;
    navigator.clipboard.writeText(messageBody);
    shiftNotesCapture.noteReflexMessageCopied(captureProperties);
    setJustCopied(true);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => {
      setJustCopied(false);
    }, COPIED_FEEDBACK_MS);
  };

  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  const handleMessageKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <ReflexStepCard
      label={
        label ??
        (totalSteps === 1
          ? t('instantReflex.todoLabel')
          : t('instantReflex.stepLabel', { n: stepNumber }))
      }
      labelColor={colours.blue[500]}
      icon={<General.CheckCircle />}
      title={step.title}
    >
      {hasMessage && (
        <Styled.MessageSection>
          <Styled.MessageLabel>
            {t('instantReflex.messageLabel')}
          </Styled.MessageLabel>
          <Styled.MessageBody
            role="button"
            tabIndex={0}
            aria-expanded={isExpanded}
            onClick={toggleExpanded}
            onKeyDown={handleMessageKeyDown}
            $isExpanded={isExpanded}
          >
            <Styled.MessageBodyText $isExpanded={isExpanded}>
              {emailBody || messageBody}
            </Styled.MessageBodyText>
          </Styled.MessageBody>
          <Styled.ActionsRow>
            <Styled.ActionPill
              type="button"
              onClick={handleCopy}
              aria-live="polite"
              aria-label={t('instantReflex.copy')}
            >
              <General.Copy03 />
              {justCopied ? t('instantReflex.copied') : t('instantReflex.copy')}
            </Styled.ActionPill>
            <EmailComposeMenu
              payload={{ subject, body: emailBody }}
              onSelected={(provider) =>
                shiftNotesCapture.noteReflexMessageSent({
                  ...captureProperties,
                  provider,
                })
              }
              renderTrigger={({ toggle }) => (
                <Styled.ActionPill
                  type="button"
                  onClick={toggle}
                  aria-label={t('instantReflex.share')}
                >
                  <Communication.Send01 />
                  {t('instantReflex.share')}
                </Styled.ActionPill>
              )}
            />
          </Styled.ActionsRow>
        </Styled.MessageSection>
      )}
    </ReflexStepCard>
  );
}
